import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Input, Modal, Space, Table, Tag, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { useAuth } from "contexts/AuthContext";
import CatalogItemDrawer from "./CatalogItemDrawer";
import CatalogItemDetailsDrawer from "./CatalogItemDetailsDrawer";
import { formatText, statusColor, tagStatusText, toMessage } from "./utils";
import { toast } from "react-toastify";

export default function CatalogItemsPage({
  title,
  subtitle,
  icon,
  newLabel,
  itemLabel,
  showDelete = true,
  loadItems,
  createItem,
  updateItem,
  deleteItem,
  getItem,
  normalizeSearch,
  rowKey = "id",
  codeReadOnly = false,
  buildCreatePayload = (values) => values,
  buildUpdatePayload = (values) => values,
}) {
  const { user } = useAuth();
  const actorId = user?.id || "";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchItems = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await loadItems(params);
      const content = Array.isArray(res) ? res : Array.isArray(res?.content) ? res.content : [];
      setItems(content);
      return res;
    } catch (err) {
      const message = toMessage(err, `Failed to load ${itemLabel.toLowerCase()}s`);
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadItems, itemLabel]);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim()) {
        fetchItems({ search: query.trim() });
      } else {
        fetchItems();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, fetchItems]);

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    setDrawerOpen(true);
  };

  const handleView = useCallback(async (record) => {
    if (getItem && record?.id) {
      try {
        const fresh = await getItem(record);
        setSelected(fresh || record);
      } catch {
        setSelected(record);
      }
    } else {
      setSelected(record);
    }
    setDetailsOpen(true);
  }, [getItem]);

  const handleDelete = useCallback((record) => {
    Modal.confirm({
      title: `Remove ${itemLabel.toLowerCase()}?`,
      content: `This will delete the ${itemLabel.toLowerCase()}.`,
      okText: "Remove",
      okButtonProps: { danger: true, loading: deleting },
      onOk: async () => {
        setDeleting(true);
        try {
          await deleteItem(record, actorId);
          await fetchItems();
          toast.success(`${itemLabel} removed`);
        } catch (err) {
          toast.error(toMessage(err, `Failed to delete ${itemLabel.toLowerCase()}`));
        } finally {
          setDeleting(false);
        }
      },
    });
  }, [actorId, deleteItem, fetchItems, itemLabel, deleting]);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await updateItem(editing, buildUpdatePayload(values), actorId);
        toast.success(`${itemLabel} updated`);
      } else {
        await createItem(buildCreatePayload(values), actorId);
        toast.success(`${itemLabel} created`);
      }
      setDrawerOpen(false);
      await fetchItems();
    } catch (err) {
      toast.error(toMessage(err, `Failed to save ${itemLabel.toLowerCase()}`));
    } finally {
      setSaving(false);
    }
  };

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return (items || []).filter((item) => {
      const haystack = normalizeSearch ? normalizeSearch(item) : Object.values(item || {}).join(" ");
      return String(haystack).toLowerCase().includes(q);
    });
  }, [items, normalizeSearch, query]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Name",
        key: "name",
        ellipsis: true,
        render: (_, record) => {
          const text = record?.name || record?.title || "-";
          return (
            <Tooltip title={text} placement="topLeft">
              <span style={{ fontWeight: 600 }}>{formatText(text, 40)}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Code",
        dataIndex: "code",
        key: "code",
        ellipsis: true,
        render: (value) => {
          const text = value || "-";
          return (
            <Tooltip title={text} placement="topLeft">
              <span>{formatText(text, 40)}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "description",
        ellipsis: true,
        render: (value) => {
          const text = formatText(value, 48);
          return (
            <Tooltip title={value || "-"} placement="topLeft">
              <span>{text}</span>
            </Tooltip>
          );
        },
      },
      {
        title: "Status",
        key: "status",
        render: (_, record) => {
          const active = record?.active;
          const status = record?.status;
          return active === undefined ? (
            <Tag color={statusColor(status)}>{tagStatusText(status)}</Tag>
          ) : (
            <Tag color={active ? "green" : "red"}>{active ? "ACTIVE" : "INACTIVE"}</Tag>
          );
        },
      },
    ];

    if (title === "Profession Roles") {
      baseColumns.splice(1, 0, {
        title: "Synonyms",
        key: "synonyms",
        ellipsis: true,
        render: (_, record) => {
          const synonyms = Array.isArray(record?.synonyms) ? record.synonyms : [];
          const text = synonyms.length ? synonyms.slice(0, 3).join(", ") : "-";
          const display = formatText(text, 40);
          return (
            <Tooltip title={text || "-"} placement="topLeft">
              <span>{display}</span>
            </Tooltip>
          );
        },
      });
    }

    baseColumns.push({
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <div
          className="prof-jobs-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "view",
                  label: "View",
                  icon: <EyeOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleView(record);
                  },
                },
                {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: ({ domEvent }) => {
                    domEvent.stopPropagation();
                    handleEdit(record);
                  },
                },
                ...(showDelete
                  ? [
                      {
                        key: "delete",
                        label: "Delete",
                        icon: <DeleteOutlined />,
                        danger: true,
                        onClick: ({ domEvent }) => {
                          domEvent.stopPropagation();
                          handleDelete(record);
                        },
                      },
                    ]
                  : []),
              ],
            }}
          >
            <Button size="small" type="text" aria-label="Actions">
              <EllipsisOutlined style={{ fontSize: 18 }} />
            </Button>
          </Dropdown>
        </div>
      ),
    });

    return baseColumns;
  }, [showDelete, title, handleDelete, handleView]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon={icon}
          title={title}
          subtitle={subtitle}
          className="mb-2 mt-2 flex-1"
        />
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchItems} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {newLabel}
          </Button>
        </Space>
      </div>

      <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 8 }}>
        <Input.Search
          allowClear
          placeholder={`Search by ${itemLabel.toLowerCase()}`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: "100%", maxWidth: 320 }}
        />
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={fetchItems}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Table
        rowKey={rowKey}
        loading={loading}
        columns={columns}
        dataSource={filteredItems}
        className="custom-table"
        bordered
        tableLayout="fixed"
        scroll={{ x: "max-content" }}
        size="small"
        locale={{
          emptyText: `No ${itemLabel.toLowerCase()}s found.`,
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e?.target?.closest?.(".prof-jobs-actions")) return;
            handleView(record);
          },
        })}
      />

      <CatalogItemDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        loading={saving}
        initialValues={editing}
        title={itemLabel}
        codeReadOnly={codeReadOnly}
      />

      <CatalogItemDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        item={selected}
        title={itemLabel}
        onEdit={(record) => {
          setDetailsOpen(false);
          handleEdit(record);
        }}
      />
    </div>
  );
}
